import { useMemo } from "react";
import {
  type AwardCriterionType,
  LibraryElementTypeDto,
  useListLibraryElementsQuery,
} from "../../autogen/bff-api";
import { criterionTypeSelectValue } from "../procurement/publicProcurementOptions";

/**
 * Whether the library holds an award criterion of one family — asked before offering a route into it.
 *
 * A button that opens an empty panel is worse than no button: it reads as a route the user failed to
 * use rather than one that was never there. So the surfaces that offer "pick one from the library"
 * ask first, and stay quiet when there is nothing to pick.
 *
 * The family is collapsed the way the rest of the wizard collapses it, so a criterion stored under any
 * environment sub-type answers for "Miljø" — the BFF re-derives that sub-type from the total weighting,
 * and a criterion the buyer filed as environmental must not disappear because of it.
 */
export const useHasLibraryAwardCriterionOfType = (
  type: AwardCriterionType,
): boolean => {
  const { data } = useListLibraryElementsQuery({});
  const wanted = criterionTypeSelectValue(type);

  return useMemo(
    () =>
      (data?.elements ?? []).some(
        (element) =>
          element.content.type === LibraryElementTypeDto.Award &&
          element.content.award != null &&
          criterionTypeSelectValue(element.content.award.type) === wanted
      ),
    [data?.elements, wanted]
  );
};
